import { ReactNode } from 'react';
import styled from 'styled-components';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Dimensions, Metrics } from 'components/insights/constants';
import { EM_DASH, formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';

import { buildEntityCell } from '../../shared/slideout/expandableRowStyles';
import { findRollupByGroupingValue, groupingValueFor, totalDimsFor } from '../../shared/slideout/rollupHelpers';
import {
  CHOSE_DIFFERENT_FACILITY,
  denialReasonLabel,
  INBOUND_ENTITY_DIMENSIONS,
  isRealDimensionValue,
  notSelectedReasonLabel,
} from '../constants';

export type InboundReferralTableRow = {
  entityName: string;
  // The filter value that scopes a drill to this row: the group id for facility, else the text
  // value. Empty when the row has no name ("None") — those rows aren't clickable.
  entityId: string;
  referrals: number;
  acceptanceRate: number | null;
  denialRate: number | null;
  pendingRate: number | null;
  avgTimeToDecision: number | null;
};

export type InboundEntityCellRenderer = (info: CellContext<InboundReferralTableRow, string>) => ReactNode;

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const FIRST_CELL_PADDING = { meta: { padding: '16px 12px 16px 24px' } };
const STRETCH_CELL = { meta: { padding: '16px 12px', style: { alignSelf: 'stretch' } } };

// Rows are the per-entity detail rows (grouping 0) for the single View-By dimension. When the
// dimension carries an id column (facility, via `:with_id`), the id sits at index 1 and becomes the
// row's entityId; otherwise the text name is its own id.
export function buildInboundRows(data: AnalyticsResponseData, dimension?: Dimensions): InboundReferralTableRow[] {
  const entityNames = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const hasIdColumn = dimension ? INBOUND_ENTITY_DIMENSIONS.has(dimension) : false;
  const entityIds = hasIdColumn ? data.dimensions.getCurrentDimensionValuesByIndex(1) : [];
  const referralCounts = data.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT);
  const acceptanceRates = data.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
  const denialRates = data.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
  const pendingRates = data.metrics.getCurrentSeries(Metrics.PENDING_RATE);
  const decisionTimes = data.metrics.getCurrentSeries(Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE);

  return entityNames.map((entityName, i) => ({
    entityName: entityName || 'None',
    entityId: hasIdColumn ? (entityIds[i] ?? '') : (entityName ?? ''),
    referrals: referralCounts[i] ?? 0,
    acceptanceRate: acceptanceRates[i] ?? null,
    denialRate: denialRates[i] ?? null,
    pendingRate: pendingRates[i] ?? null,
    avgTimeToDecision: decisionTimes[i] ?? null,
  }));
}

const defaultEntityCell: InboundEntityCellRenderer = (info) => <EntityName>{info.getValue()}</EntityName>;

export function buildInboundColumns(
  entityHeader: string,
  entityCell: InboundEntityCellRenderer = defaultEntityCell
): ColumnDef<InboundReferralTableRow, any>[] {
  const helper = createColumnHelper<InboundReferralTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: entityCell,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('referrals', {
      header: 'Total Referrals',
      cell: (info) => info.getValue(),
      size: 150,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('acceptanceRate', {
      header: 'Acceptance Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 165,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('denialRate', {
      header: 'Denial Rate (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 140,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('pendingRate', {
      header: 'Pending Rate (%)',
      cell: (info) => formatPercentage(info.getValue()),
      size: 140,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor('avgTimeToDecision', {
      header: 'Avg. Time to Facility Decision',
      cell: (info) => formatResponseTime(info.getValue()),
      size: 230,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

// ----- Accepted metric drill (OLIO-2896) breakdown table -----

export type InboundAcceptedTableRow = {
  entityName: string;
  received: number;
  accepted: number;
  acceptanceRate: number | null;
  avgTimeToAcceptance: number | null;
};

export function buildAcceptedRows(data: AnalyticsResponseData): InboundAcceptedTableRow[] {
  const entityNames = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const received = data.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT);
  const accepted = data.metrics.getCurrentSeries(Metrics.ACCEPTED_COUNT);
  const acceptanceRates = data.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
  const acceptedTimes = data.metrics.getCurrentSeries(Metrics.TIME_TO_ACCEPTED_DECISION_SECONDS_AVERAGE);

  return entityNames.map((entityName, i) => ({
    entityName: entityName || 'None',
    received: received[i] ?? 0,
    accepted: accepted[i] ?? 0,
    acceptanceRate: acceptanceRates[i] ?? null,
    avgTimeToAcceptance: acceptedTimes[i] ?? null,
  }));
}

export function buildAcceptedColumns(entityHeader: string): ColumnDef<InboundAcceptedTableRow, any>[] {
  const helper = createColumnHelper<InboundAcceptedTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: (info) => <EntityName>{info.getValue()}</EntityName>,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('received', {
      header: 'Total Received',
      cell: (info) => info.getValue(),
      size: 135,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('accepted', {
      header: 'Total Accepted',
      cell: (info) => info.getValue(),
      size: 140,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('acceptanceRate', {
      header: 'Acceptance Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 160,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('avgTimeToAcceptance', {
      header: 'Avg. Time to Acceptance',
      cell: (info) => formatResponseTime(info.getValue()),
      size: 180,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

// ----- Selected metric drill (OLIO-2898) breakdown table -----

export type InboundSelectedTableRow = {
  entityName: string;
  // "Total Closed" = buildings that reached a recorded outcome = selected + not_selected. Selection
  // is gate-free, so this is the right denominator context (not accepted, which excludes a
  // pending/denied building that was still selected).
  closed: number;
  selected: number;
  selectedRate: number | null;
};

export function buildSelectedRows(data: AnalyticsResponseData): InboundSelectedTableRow[] {
  const entityNames = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const selected = data.metrics.getCurrentSeries(Metrics.SELECTED_COUNT);
  const closed = data.metrics.getCurrentSeries(Metrics.CLOSED_COUNT);
  const selectedRates = data.metrics.getCurrentSeries(Metrics.SELECTED_RATE);

  return (
    entityNames
      .map((entityName, i) => ({
        entityName: entityName || 'None',
        closed: closed[i] ?? 0,
        selected: selected[i] ?? 0,
        selectedRate: selectedRates[i] ?? null,
      }))
      // Drop entities with nothing closed (e.g. a referral/payer source whose referrals never reached
      // an outcome) — an all-zero row with an em-dash rate, not a real breakdown row.
      .filter((row) => row.closed > 0)
  );
}

export function buildSelectedColumns(entityHeader: string): ColumnDef<InboundSelectedTableRow, any>[] {
  const helper = createColumnHelper<InboundSelectedTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: (info) => <EntityName>{info.getValue()}</EntityName>,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('closed', {
      header: 'Total Closed',
      cell: (info) => info.getValue(),
      size: 105,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('selected', {
      header: 'Total Selected',
      cell: (info) => info.getValue(),
      size: 130,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('selectedRate', {
      header: 'Selected Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 150,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
  ];
}

// ----- Denied metric drill (OLIO-2897) breakdown table -----

export type InboundDeniedTableRow = {
  entityName: string;
  entityId: string;
  // Undefined on sub-rows: a reason breakdown has no "received" of its own, so the Total Received
  // cell shows an em-dash for reason rows.
  received?: number;
  denied: number;
  denialRate: number | null;
  avgTimeToDenial: number | null;
  isSubRow?: boolean;
  subRows?: InboundDeniedTableRow[];
};

// The Denied breakdown query is [entity, denial_reason_category, denial_reason_description] with
// rollups. Parent rows come from the entity-level rollup (received/denied/rate/time with the reason
// dims rolled up); the reason sub-rows come from the grouping-0 leaves (in `data`), one per
// (category, description). A sub-row's rate is its denials over the parent's received — the leaf's
// own denial_rate would always be 100% (every leaf row is a denial of that reason).
export function buildDeniedRowsWithSubRows(
  data: AnalyticsResponseData,
  rollups: AnalyticsResponseRollupCollection
): InboundDeniedTableRow[] {
  const totalDims = totalDimsFor(rollups);
  const parentRollup = findRollupByGroupingValue(rollups, groupingValueFor(totalDims, new Set([0])));
  if (!parentRollup) return [];

  const names = parentRollup.dimensions.getCurrentDimensionValuesByIndex(0);
  const received = parentRollup.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT);
  const denied = parentRollup.metrics.getCurrentSeries(Metrics.DENIED_COUNT);
  const denialRate = parentRollup.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
  const times = parentRollup.metrics.getCurrentSeries(Metrics.TIME_TO_DENIAL_DECISION_SECONDS_AVERAGE);

  const parents: InboundDeniedTableRow[] = names.map((name, i) => ({
    entityName: name || 'None',
    entityId: name ?? '',
    received: received[i] ?? 0,
    denied: denied[i] ?? 0,
    denialRate: denialRate[i] ?? null,
    avgTimeToDenial: times[i] ?? null,
  }));

  const subsByParent = groupDeniedSubRows(data);

  return parents.map((parent) => {
    const subs = subsByParent.get(parent.entityId);
    if (!subs?.length) return parent;
    const denominator = parent.received ?? 0;
    return {
      ...parent,
      subRows: subs.map((sub) => ({
        ...sub,
        denialRate: denominator > 0 ? (sub.denied / denominator) * 100 : null,
      })),
    };
  });
}

// Group the grouping-0 leaves into reason sub-rows keyed by their parent entity, dropping the
// non-denied leaf. That leaf is identified by denied == 0 (its NULL category/description format to
// the literal 'None', so they never read as falsy) — denied > 0 is the real filter. The parent key
// is the entity name, matching the parent rows' entityId.
function groupDeniedSubRows(data: AnalyticsResponseData): Map<string, InboundDeniedTableRow[]> {
  const parentKeys = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const categories = data.dimensions.getCurrentDimensionValuesByIndex(1);
  const descriptions = data.dimensions.getCurrentDimensionValuesByIndex(2);
  const denied = data.metrics.getCurrentSeries(Metrics.DENIED_COUNT);
  const times = data.metrics.getCurrentSeries(Metrics.TIME_TO_DENIAL_DECISION_SECONDS_AVERAGE);

  const byParent = new Map<string, InboundDeniedTableRow[]>();
  descriptions.forEach((description, i) => {
    const category = categories[i];
    const parentKey = parentKeys[i];
    if (!parentKey || (denied[i] ?? 0) <= 0) return;
    const row: InboundDeniedTableRow = {
      entityName: denialReasonLabel(category, description),
      entityId: `${parentKey}:${description}`,
      denied: denied[i] ?? 0,
      denialRate: null,
      avgTimeToDenial: times[i] ?? null,
      isSubRow: true,
    };
    const list = byParent.get(parentKey);
    if (list) list.push(row);
    else byParent.set(parentKey, [row]);
  });
  return byParent;
}

export function buildDeniedColumns(entityHeader: string): ColumnDef<InboundDeniedTableRow, any>[] {
  const helper = createColumnHelper<InboundDeniedTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: buildEntityCell,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('received', {
      header: 'Total Received',
      // Reason sub-rows have no received of their own (it's the parent's) — show an em-dash, not blank.
      cell: (info) => (info.row.original.isSubRow ? EM_DASH : info.getValue()),
      size: 135,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('denied', {
      header: 'Total Denied',
      cell: (info) => info.getValue(),
      size: 135,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('denialRate', {
      header: 'Denial Rate (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 145,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('avgTimeToDenial', {
      header: 'Avg. Time to Denial',
      cell: (info) => formatResponseTime(info.getValue()),
      size: 170,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

// ----- Not Selected metric drill (OLIO-2899) breakdown table -----

export type InboundNotSelectedTableRow = {
  entityName: string;
  entityId: string;
  // Entity-level "Total Closed" (decided = selected + not_selected) — the leading column value AND
  // the denominator for each reason sub-row's share. Blank/unset on sub-rows.
  closed?: number;
  notSelected: number;
  notSelectedRate: number | null;
  isSubRow?: boolean;
  subRows?: InboundNotSelectedTableRow[];
};

// The Not Selected breakdown query is [entity, not_selected_reason] with rollups. Parent rows come
// from the entity-level rollup; the reason sub-rows come from the grouping-0 leaves (one per
// not_selected_reason). A sub-row's rate is its not-selected count over the parent's "referrals
// closed" (the not_selected_rate denominator) — the leaf's own not_selected_rate would always be
// 100%. Unlike Denied, the NULL-reason leaf is KEPT as a synthetic "Patient chose different
// facility" bucket so the sub-rows sum to the parent's Not Selected count.
export function buildNotSelectedRowsWithSubRows(
  data: AnalyticsResponseData,
  rollups: AnalyticsResponseRollupCollection
): InboundNotSelectedTableRow[] {
  const totalDims = totalDimsFor(rollups);
  const parentRollup = findRollupByGroupingValue(rollups, groupingValueFor(totalDims, new Set([0])));
  if (!parentRollup) return [];

  const names = parentRollup.dimensions.getCurrentDimensionValuesByIndex(0);
  const notSelected = parentRollup.metrics.getCurrentSeries(Metrics.NOT_SELECTED_COUNT);
  const notSelectedRate = parentRollup.metrics.getCurrentSeries(Metrics.NOT_SELECTED_RATE);
  const closed = parentRollup.metrics.getCurrentSeries(Metrics.CLOSED_COUNT);

  const parents: InboundNotSelectedTableRow[] = names
    .map((name, i) => ({
      entityName: name || 'None',
      entityId: name ?? '',
      closed: closed[i] ?? 0,
      notSelected: notSelected[i] ?? 0,
      notSelectedRate: notSelectedRate[i] ?? null,
    }))
    // Drop entities with nothing closed (e.g. a referral/payer source whose referrals never reached
    // an outcome) — an all-zero row with an em-dash rate, not a real breakdown row.
    .filter((parent) => (parent.closed ?? 0) > 0);

  const subsByParent = groupNotSelectedSubRows(data);

  return parents.map((parent) => {
    const subs = subsByParent.get(parent.entityId);
    if (!subs?.length) return parent;
    const denominator = parent.closed ?? 0;
    return {
      ...parent,
      subRows: subs.map((sub) => ({
        ...sub,
        notSelectedRate: denominator > 0 ? (sub.notSelected / denominator) * 100 : null,
      })),
    };
  });
}

// Group the grouping-0 leaves into reason sub-rows keyed by their parent entity. The NULL-reason
// leaf is KEPT (labeled "Patient chose different facility"), unlike the denied grouping which drops
// its NULL leaf.
function groupNotSelectedSubRows(data: AnalyticsResponseData): Map<string, InboundNotSelectedTableRow[]> {
  const parentKeys = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const reasons = data.dimensions.getCurrentDimensionValuesByIndex(1);
  const notSelected = data.metrics.getCurrentSeries(Metrics.NOT_SELECTED_COUNT);

  const byParent = new Map<string, InboundNotSelectedTableRow[]>();
  reasons.forEach((reason, i) => {
    const parentKey = parentKeys[i];
    if (!parentKey || (notSelected[i] ?? 0) <= 0) return;
    const label = isRealDimensionValue(reason)
      ? notSelectedReasonLabel(reason)
      : notSelectedReasonLabel(CHOSE_DIFFERENT_FACILITY);
    const row: InboundNotSelectedTableRow = {
      entityName: label,
      entityId: `${parentKey}:${isRealDimensionValue(reason) ? reason : CHOSE_DIFFERENT_FACILITY}`,
      notSelected: notSelected[i] ?? 0,
      notSelectedRate: null,
      isSubRow: true,
    };
    const list = byParent.get(parentKey);
    if (list) list.push(row);
    else byParent.set(parentKey, [row]);
  });
  return byParent;
}

export function buildNotSelectedColumns(entityHeader: string): ColumnDef<InboundNotSelectedTableRow, any>[] {
  const helper = createColumnHelper<InboundNotSelectedTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: buildEntityCell,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('closed', {
      header: 'Total Closed',
      // Reason sub-rows have no closed count of their own (it's the parent's) — em-dash, not blank.
      cell: (info) => (info.row.original.isSubRow ? EM_DASH : info.getValue()),
      size: 105,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('notSelected', {
      header: 'Total Not Selected',
      cell: (info) => info.getValue(),
      size: 135,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('notSelectedRate', {
      header: 'Not Selected (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 150,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
  ];
}

// ----- Admitted metric drill (OLIO-2900) breakdown table -----

export type InboundAdmittedTableRow = {
  entityName: string;
  selected: number;
  admitted: number;
  admissionRate: number | null;
};

export function buildAdmittedRows(data: AnalyticsResponseData): InboundAdmittedTableRow[] {
  const entityNames = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const selected = data.metrics.getCurrentSeries(Metrics.SELECTED_COUNT);
  const admitted = data.metrics.getCurrentSeries(Metrics.ADMITTED_COUNT);
  const admissionRates = data.metrics.getCurrentSeries(Metrics.ADMISSION_RATE);

  return (
    entityNames
      .map((entityName, i) => ({
        entityName: entityName || 'None',
        selected: selected[i] ?? 0,
        admitted: admitted[i] ?? 0,
        admissionRate: admissionRates[i] ?? null,
      }))
      // Drop entities with nothing selected (e.g. a referral/payer source whose referrals were never
      // selected) — admission rate is a % of selected, so these are all-zero em-dash rows.
      .filter((row) => row.selected > 0)
  );
}

export function buildAdmittedColumns(entityHeader: string): ColumnDef<InboundAdmittedTableRow, any>[] {
  const helper = createColumnHelper<InboundAdmittedTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: (info) => <EntityName>{info.getValue()}</EntityName>,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('selected', {
      header: 'Total Selected',
      cell: (info) => info.getValue(),
      size: 130,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('admitted', {
      header: 'Total Admitted',
      cell: (info) => info.getValue(),
      size: 135,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('admissionRate', {
      header: 'Selected to Admitted Conversion',
      cell: (info) => formatPercentage(info.getValue()),
      size: 240,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

const EntityName = styled.span`
  color: ${({ theme }) => theme.colors.black100};
  font-weight: var(--font-weight-medium);
`;

// Clickable entity name that opens the row's entity drill. Stretches to fill the cell so the whole
// first column is the hit target, matching the outbound referrals table.
export const EntityButton = styled.button`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
  width: calc(100% + 36px);
  margin: -16px -12px -16px -24px;
  padding: 16px 12px 16px 24px;
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: var(--font-weight-medium);
  white-space: normal;
  overflow-wrap: anywhere;

  td:hover & {
    text-decoration: underline;
  }
`;

const GreenText = styled.span`
  color: ${({ theme }) => theme.colors.green100};
  background-color: ${colors.green5};
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
`;

const RedText = styled.span`
  color: ${({ theme }) => theme.colors.red100};
  background-color: ${colors.red5};
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
  width: 100%;
`;
